// src/features/task/TaskWorkPlanning.tsx
// Workforce planning for a task: block time for the work in the assignee's
// calendar (distinct from due_date — this is "when work is planned"). Wraps the
// TaskService scheduling RPCs ScheduleTask / RescheduleTask / UnscheduleTask /
// SuggestTaskSchedule. (Ad-hoc entity scheduling lives in the calendar's own
// EntitySchedulePanel; this panel is the task-service workforce path.)

import { useState } from 'react';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
} from '@react/components';
import { formatDateTime } from '@/features/task/logic';
import { errMsg, isoToLocal, localToIso, secondsToDuration } from '@/features/task/panelHelpers';

interface TaskWorkPlanningProps {
  taskId: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  editable?: boolean;
  /** Render without the Card chrome; the caps label stays, the frame goes. */
  bare?: boolean;
  onChanged?: () => void;
}

export const TaskWorkPlanning = observer(({
  taskId,
  scheduledStart,
  scheduledEnd,
  editable = true,
  bare = false,
  onChanged,
}: TaskWorkPlanningProps) => {
  const store = useTask();
  const { t, locale } = useI18n();

  const hasSchedule = !!scheduledStart;

  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState(isoToLocal(scheduledStart));
  const [end, setEnd] = useState(isoToLocal(scheduledEnd));
  const [reason, setReason] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [suggestEmpty, setSuggestEmpty] = useState(false);

  const openForm = () => {
    setStart(isoToLocal(scheduledStart) || isoToLocal(new Date().toISOString()));
    setEnd(isoToLocal(scheduledEnd));
    setReason('');
    setError(null);
    setSuggestEmpty(false);
    setFormOpen(true);
  };

  const suggest = async () => {
    setBusy(true);
    setError(null);
    setSuggestEmpty(false);
    try {
      const res: any = await store.SuggestTaskSchedule(taskId, {
        respect_working_hours: true,
        max_suggestions: 5,
      } as any);
      const list = res?.suggestions || [];
      setSuggestions(list);
      // No candidates (task unassigned) or no free slots — say so instead of
      // silently opening an empty form.
      setSuggestEmpty(list.length === 0);
      if (!formOpen) setFormOpen(true);
    } catch (e) {
      setError(errMsg(e, t('task.workPlan.suggestError')));
    } finally {
      setBusy(false);
    }
  };

  const applySuggestion = (s: any) => {
    setStart(isoToLocal(s.start_time));
    setEnd(isoToLocal(s.end_time));
  };

  const submit = async () => {
    if (!start) {
      setError(t('task.workPlan.startRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (hasSchedule) {
        await store.RescheduleTask(taskId, {
          new_start_time: localToIso(start),
          new_end_time: end ? localToIso(end) : undefined,
          reason: reason.trim() || undefined,
          notify_assignee: true,
        } as any);
      } else {
        await store.ScheduleTask(taskId, {
          start_time: localToIso(start),
          end_time: end ? localToIso(end) : localToIso(start),
          notify_assignee: true,
        } as any);
      }
      setFormOpen(false);
      setSuggestions([]);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.workPlan.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const unschedule = async () => {
    setBusy(true);
    setError(null);
    try {
      await store.UnscheduleTask(taskId);
      setFormOpen(false);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.workPlan.unscheduleError')));
    } finally {
      setBusy(false);
    }
  };

  // Log the planned block as actual time worked: duration is end − start.
  const logActual = async () => {
    if (!scheduledStart) return;
    setBusy(true);
    setError(null);
    try {
      const startMs = new Date(scheduledStart).getTime();
      const endMs = scheduledEnd ? new Date(scheduledEnd).getTime() : startMs;
      await store.LogTime(taskId, {
        duration: secondsToDuration(Math.max(0, Math.round((endMs - startMs) / 1000))),
        started_at: new Date(startMs).toISOString(),
      } as any);
      onChanged?.();
    } catch (e) {
      setError(errMsg(e, t('task.workPlan.logActualError')));
    } finally {
      setBusy(false);
    }
  };

  const content = (
    <>
      {error && <Alert variant="error">{error}</Alert>}

      {hasSchedule ? (
        <div className="text-sm text-foreground">
          <p className="font-medium tabular-nums">
            {formatDateTime(scheduledStart, locale)}
            {scheduledEnd && ` – ${formatDateTime(scheduledEnd, locale)}`}
          </p>
          <Badge variant="secondary" size="sm" className="mt-1">{t('task.workPlan.planned')}</Badge>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('task.workPlan.notPlanned')}</p>
      )}

        {editable && !formOpen && (
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="secondary" onClick={openForm}>
              {hasSchedule ? t('task.workPlan.reschedule') : t('task.workPlan.schedule')}
            </Button>
            <Button size="sm" variant="ghost" onClick={suggest} isLoading={busy}>
              {t('task.workPlan.suggest')}
            </Button>
            {hasSchedule && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={logActual}>
                {t('task.workPlan.logActual')}
              </Button>
            )}
            {hasSchedule && (
              <Button size="sm" variant="danger" disabled={busy} onClick={unschedule}>
                {t('task.workPlan.unschedule')}
              </Button>
            )}
          </div>
        )}

        {formOpen && (
          <div className="space-y-2 rounded-md border border-border p-2">
            {suggestEmpty && (
              <p className="text-xs text-muted-foreground">{t('task.workPlan.noSuggestions')}</p>
            )}
            {suggestions.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('task.workPlan.suggestions')}</p>
                {suggestions.map((s: any, idx: number) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => applySuggestion(s)}
                    className="w-full text-left rounded-md border border-border px-2 py-1 text-xs text-foreground hover:bg-muted"
                  >
                    {formatDateTime(s.start_time, locale)}
                    {s.end_time && ` – ${formatDateTime(s.end_time, locale)}`}
                    {typeof s.score === 'number' && (
                      <span className="ml-2 text-muted-foreground">{Math.round(s.score * 100)}%</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <label className="flex flex-col text-xs text-muted-foreground">
              {t('task.workPlan.start')}
              <input
                type="datetime-local"
                aria-label={t('task.workPlan.start')}
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
            </label>
            <label className="flex flex-col text-xs text-muted-foreground">
              {t('task.workPlan.end')}
              <input
                type="datetime-local"
                aria-label={t('task.workPlan.end')}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
            </label>
            {hasSchedule && (
              <input
                type="text"
                aria-label={t('task.workPlan.reason')}
                placeholder={t('task.workPlan.reason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
              />
            )}
            <div className="flex gap-1.5">
              <Button size="sm" onClick={submit} isLoading={busy}>
                {hasSchedule ? t('task.workPlan.reschedule') : t('task.workPlan.schedule')}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setFormOpen(false); setSuggestions([]); }}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (bare) {
    // No own label: the host's group header (e.g. Details → Time) carries it.
    return <div className="space-y-3">{content}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.workPlan.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {content}
      </CardContent>
    </Card>
  );
});

export default TaskWorkPlanning;
